import React, { useState, useEffect, useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, BarController, LineController } from 'chart.js';
import { Line } from 'react-chartjs-2';
import StatTile from '../ui/StatTile';
import { getDNColorName } from '../../utils/dnUtils';
import { API_BASE_URL } from '../../config';
import LoadingSpinner from '../LoadingSpinner';
import { useDN } from '../../contexts/DNContext';
import { _t } from '../../utils/i18n';

// Register the required Chart.js components
ChartJS.register(
  BarController,
  LineController,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

// Couleurs prédéfinies pour les différentes courbes de produits
const productColors = [
  { border: 'rgb(75, 192, 192)', background: 'rgba(75, 192, 192, 0.5)' },
  { border: 'rgb(255, 99, 132)', background: 'rgba(255, 99, 132, 0.5)' },
  { border: 'rgb(255, 205, 86)', background: 'rgba(255, 205, 86, 0.5)' },
  { border: 'rgb(54, 162, 235)', background: 'rgba(54, 162, 235, 0.5)' },
  { border: 'rgb(153, 102, 255)', background: 'rgba(153, 102, 255, 0.5)' }
];

function DNChart({ selectedProducts, selectedProject, fetchWithAuth, selectedShopSigns }) {
  const multiProduct = selectedProducts.length > 1;

  const [showVisitedStores, setShowVisitedStores] = useState(true);
  const [loading, setLoading] = useState(false);
  const [rawChartData, setRawChartData] = useState([]);
  const [filteredChartData, setFilteredChartData] = useState([]);
  const [error, setError] = useState(null);

  const { startDate, endDate } = useDN();

  const isDarkMode = localStorage.getItem('darkMode') === 'true';

  // Fetch raw data from the API
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedProject?.value || !selectedProducts?.length) return;

      setLoading(true);
      setError(null);

      try {
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        // Construire l'URL de base avec les paramètres communs
        let url = `${API_BASE_URL}/api/dwh/dn/${selectedProject.value}/products/aggregated-measures-shop-sign?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

        // Ajouter chaque produit sélectionné à l'URL
        selectedProducts.forEach(product => {
          url += `&products[]=${encodeURIComponent(product.value)}`;
        });

        const response = await fetchWithAuth(url);

        if (response.ok) {
          const data = await response.json();
          setRawChartData(data);
        } else {
          setError(_t('Error fetching data'));
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(_t('An error occurred while fetching data'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedProject, selectedProducts, startDate, endDate, fetchWithAuth]);

  // Filter data based on selectedShopSigns
  useEffect(() => {
    if (!rawChartData || rawChartData.length === 0) {
      setFilteredChartData([]);
      return;
    }

    if (!selectedShopSigns || selectedShopSigns.length === 0) {
      // If no shop signs selected, use all data
      setFilteredChartData(rawChartData);
    } else {
      // Filter data by selected shop signs
      const shopSignValues = selectedShopSigns.map(sign => sign.value);
      const filtered = rawChartData.filter(item => shopSignValues.includes(item.SHOP_SIGN));
      setFilteredChartData(filtered);
    }
  }, [rawChartData, selectedShopSigns]);

  // Fonction pour traiter les données par produit
  const processWeeklyData = (data, productName) => {
    if (!data || !data.length) return null;

    // Trier les données par année et semaine
    const sortedData = [...data].sort((a, b) => {
      if (a.YEAR !== b.YEAR) {
        return a.YEAR - b.YEAR;
      }
      return a.WEEK - b.WEEK;
    });

    // Créer un objet pour stocker les données par semaine pour ce produit
    const weeklyData = new Map();

    // Identifier la première et dernière semaine
    const firstItem = sortedData[0];
    const lastItem = sortedData[sortedData.length - 1];
    const startWeek = { year: firstItem.YEAR, week: firstItem.WEEK };
    const endWeek = { year: lastItem.YEAR, week: lastItem.WEEK };

    // Générer toutes les semaines entre la première et la dernière
    const allWeekKeys = generateAllWeeksBetween(startWeek, endWeek);

    // Pour chaque semaine, agréger les données de toutes les enseignes
    allWeekKeys.forEach(weekKey => {
      const [year, weekNum] = weekKey.split('-W');
      const week = parseInt(weekNum, 10);
      const yearInt = parseInt(year, 10);

      // Filtrer les données pour cette semaine et ce produit
      const weekData = sortedData.filter(item =>
        item.YEAR === yearInt &&
        item.WEEK === week &&
        item.PRODUCT === productName
      );

      if (weekData.length > 0) {
        // Agréger les données de toutes les enseignes pour cette semaine
        const aggregated = weekData.reduce((acc, curr) => {
          return {
            PRESENT_SHOPS: acc.PRESENT_SHOPS + curr.PRESENT_SHOPS,
            NOT_PRESENT_SHOPS: acc.NOT_PRESENT_SHOPS + curr.NOT_PRESENT_SHOPS,
            NO_REF_SHOPS: acc.NO_REF_SHOPS + curr.NO_REF_SHOPS
          };
        }, { PRESENT_SHOPS: 0, NOT_PRESENT_SHOPS: 0, NO_REF_SHOPS: 0 });

        weeklyData.set(weekKey, {
          weekKey,
          date: weekData[0].DATE,
          year: yearInt,
          week,
          ...aggregated
        });
      }
    });

    // Remplir les semaines manquantes avec les données de la semaine précédente
    let lastWeekData = null;

    allWeekKeys.forEach(weekKey => {
      if (!weeklyData.has(weekKey)) {
        if (lastWeekData) {
          // Copier les données de la dernière semaine connue
          weeklyData.set(weekKey, {
            ...lastWeekData,
            weekKey
          });
        } else {
          // Si on n'a pas encore de données (première semaine manquante), initialiser à zéro
          const [year, weekNum] = weekKey.split('-W');
          weeklyData.set(weekKey, {
            weekKey,
            year: parseInt(year, 10),
            week: parseInt(weekNum, 10),
            PRESENT_SHOPS: 0,
            NOT_PRESENT_SHOPS: 0,
            NO_REF_SHOPS: 0
          });
        }
      } else {
        // Mettre à jour la dernière semaine connue
        lastWeekData = weeklyData.get(weekKey);
      }
    });

    // Calculer les taux et les retourner
    const labels = allWeekKeys;
    const presentShops = allWeekKeys.map(week => weeklyData.get(week).PRESENT_SHOPS);
    const notPresentShops = allWeekKeys.map(week => weeklyData.get(week).NOT_PRESENT_SHOPS);
    const noRefShops = allWeekKeys.map(week => weeklyData.get(week).NO_REF_SHOPS);

    // Calculer les taux de DN pour chaque semaine
    const dnRates = allWeekKeys.map(week => {
      const data = weeklyData.get(week);
      const total = data.PRESENT_SHOPS + data.NOT_PRESENT_SHOPS;
      return total > 0 ? (data.PRESENT_SHOPS / total) * 100 : 0;
    });

    // Calculer les métriques finales
    const lastWeek = weeklyData.get(allWeekKeys[allWeekKeys.length - 1]);
    const totalStores = lastWeek.PRESENT_SHOPS + lastWeek.NOT_PRESENT_SHOPS + lastWeek.NO_REF_SHOPS;
    const finalStoresWithProduct = lastWeek.PRESENT_SHOPS;
    const finalDnRate = (lastWeek.PRESENT_SHOPS + lastWeek.NOT_PRESENT_SHOPS) > 0
      ? (lastWeek.PRESENT_SHOPS / (lastWeek.PRESENT_SHOPS + lastWeek.NOT_PRESENT_SHOPS)) * 100
      : 0;

    // Calculer les ratios cumulatifs pour chaque semaine
    const cumulativeVisitedStores = new Map(); // Pour suivre le nombre cumulatif de magasins visités
    let totalStoreCount = 0; // Nombre total de magasins (fixe)

    // Calculer d'abord le nombre total de magasins à la fin de la période
    totalStoreCount = lastWeek.PRESENT_SHOPS + lastWeek.NOT_PRESENT_SHOPS + lastWeek.NO_REF_SHOPS;

    // Calculer le pourcentage de couverture cumulatif
    let cumulativeVisited = 0;
    const coveragePercentage = allWeekKeys.map(weekKey => {
      const data = weeklyData.get(weekKey);

      // Magasins visités cette semaine (présent + non présent)
      const weeklyVisited = data.PRESENT_SHOPS + data.NOT_PRESENT_SHOPS;

      // Ajouter au total cumulatif (mais ne pas compter deux fois les mêmes magasins)
      // Comme on n'a pas l'identifiant des magasins, on va approximer
      // en prenant le maximum entre la semaine courante et le cumulatif précédent
      cumulativeVisited = Math.max(cumulativeVisited, weeklyVisited);

      // Calculer le pourcentage par rapport au total final
      return totalStoreCount > 0 ? (cumulativeVisited / totalStoreCount) * 100 : 0;
    });

    return {
      labels,
      dnRates,
      coveragePercentage,
      totalStores,
      finalStoresWithProduct,
      excludedStoresCount: lastWeek.NO_REF_SHOPS,
      lastDnRate: finalDnRate,
      weeklyData: Array.from(weeklyData.values())
    };
  };

  // Fonction utilitaire pour générer toutes les semaines entre deux dates
  const generateAllWeeksBetween = (start, end) => {
    const weekKeys = [];
    let currentYear = start.year;
    let currentWeek = start.week;

    while (currentYear < end.year || (currentYear === end.year && currentWeek <= end.week)) {
      weekKeys.push(`${currentYear}-W${currentWeek.toString().padStart(2, '0')}`);

      // Passer à la semaine suivante
      currentWeek++;

      // Si on dépasse la semaine 52/53, passer à l'année suivante
      if (currentWeek > 52) {
        currentYear++;
        currentWeek = 1;
      }
    }

    return weekKeys;
  };

  // Traiter les données pour tous les produits sélectionnés (utiliser filteredChartData)
  const productsChartData = useMemo(() => {
    if (!filteredChartData || filteredChartData.length === 0) return [];

    // Identifier les produits uniques
    const uniqueProducts = [...new Set(filteredChartData.map(item => item.PRODUCT))];

    // Traiter les données pour chaque produit
    return uniqueProducts.map(productName => {
      const productData = filteredChartData.filter(item => item.PRODUCT === productName);
      const processedData = processWeeklyData(productData, productName);

      return processedData ? {
        productName,
        data: processedData
      } : null;
    }).filter(item => item !== null);
  }, [filteredChartData]);

  // Combiner tous les labels de date uniques à travers tous les produits
  const allLabels = useMemo(() => {
    if (!productsChartData || productsChartData.length === 0) return [];

    const allDates = new Set();
    productsChartData.forEach(product => {
      product.data.labels.forEach(date => allDates.add(date));
    });

    return Array.from(allDates).sort();
  }, [productsChartData]);

  // Agréger les statistiques de tous les produits pour les tuiles
  const aggregatedStats = useMemo(() => {
    if (!productsChartData || productsChartData.length === 0) return {
      totalStores: 0,
      finalStoresWithProduct: 0,
      excludedStoresCount: 0,
      lastDnRate: 0
    };

    if (multiProduct) {
      // Mode multi-produits: faire une moyenne
      const stats = productsChartData.reduce((acc, product) => {
        return {
          totalStores: acc.totalStores + product.data.totalStores,
          finalStoresWithProduct: acc.finalStoresWithProduct + product.data.finalStoresWithProduct,
          excludedStoresCount: acc.excludedStoresCount + product.data.excludedStoresCount,
          lastDnRate: acc.lastDnRate + product.data.lastDnRate
        };
      }, { totalStores: 0, finalStoresWithProduct: 0, excludedStoresCount: 0, lastDnRate: 0 });

      // Calculer les moyennes pour le mode multi-produits
      if (productsChartData.length > 0) {
        stats.lastDnRate = stats.lastDnRate / productsChartData.length;
      }

      return stats;
    } else {
      // Mode produit unique
      return {
        totalStores: productsChartData[0]?.data.totalStores || 0,
        finalStoresWithProduct: productsChartData[0]?.data.finalStoresWithProduct || 0,
        excludedStoresCount: productsChartData[0]?.data.excludedStoresCount || 0,
        lastDnRate: productsChartData[0]?.data.lastDnRate || 0
      };
    }
  }, [productsChartData, multiProduct]);

  console.log('chartData', rawChartData);
  console.log('productsChartData', productsChartData);
  console.log('selectedProducts', selectedProducts);
  console.log('selectedProject', selectedProject);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-center py-4 text-red-500 dark:text-red-400">{error}</div>;
  }

  if (!productsChartData || productsChartData.length === 0) {
    return <div className="text-center py-4 text-gray-500 dark:text-gray-400">{_t('No data available for the graph')}</div>;
  }

  // Format date labels based on time grouping
  const formatDateLabel = (dateString) => {
    if (!dateString) return _t('Unknown date');

    try {
      if (dateString.includes('-W')) {
        // Week format like "2023-W01"
        const [year, week] = dateString.split('-W');
        return `S${week} ${year}`;
      } else {
        return dateString;
      }
    } catch (error) {
      console.error("Error formatting date:", error, dateString);
      return dateString;
    }
  };

  // Define theme-specific colors
  const colors = {
    line: {
      borderColor: isDarkMode ? 'rgb(75, 192, 192)' : 'rgb(34, 139, 230)',
      backgroundColor: isDarkMode ? 'rgba(75, 192, 192, 0.5)' : 'rgba(34, 139, 230, 0.5)',
      hoverBackgroundColor: isDarkMode ? 'rgb(75, 192, 192)' : 'rgb(34, 139, 230)',
    },
    bar: {
      backgroundColor: isDarkMode ? 'rgba(153, 102, 255, 0.7)' : 'rgba(134, 65, 244, 0.6)',
      borderColor: isDarkMode ? 'rgb(153, 102, 255)' : 'rgb(134, 65, 244)',
    },
    text: isDarkMode ? 'rgb(224, 224, 224)' : 'rgb(45, 55, 72)',
    grid: isDarkMode ? 'rgba(224, 224, 224, 0.1)' : 'rgba(45, 55, 72, 0.1)',
    background: isDarkMode ? 'rgb(55, 65, 81)' : 'rgb(255, 255, 255)',
  };

  // Préparer les datasets pour le graphique
  const datasets = [];

  // Ajouter un dataset de ligne pour chaque produit
  productsChartData.forEach((product, index) => {
    const color = productColors[index % productColors.length]; // Cycle through colors

    // Trouver les valeurs pour ce produit pour chaque date dans allLabels
    const rateValues = allLabels.map(date => {
      const labelIndex = product.data.labels.indexOf(date);
      if (labelIndex === -1) return null; // Pas de données pour cette date

      return product.data.dnRates[labelIndex];
    });

    datasets.push({
      type: 'line',
      label: multiProduct ? `${product.productName} - ${_t('DN rate (%)')}` : _t('DN rate (%)'),
      data: rateValues,
      borderColor: color.border,
      backgroundColor: color.background,
      tension: 0.5,
      borderWidth: 4,
      fill: false,
      yAxisID: 'y',
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: color.border,
      pointHoverBorderColor: colors.background,
      pointHoverBorderWidth: 2,
      order: 1,
      spanGaps: true,
      segment: {
        borderColor: ctx => {
          // Garder la couleur constante même s'il y a des données manquantes
          return color.border;
        }
      }
    });
  });

  // Si on veut montrer la couverture des magasins, ajouter un dataset de barres
  if (showVisitedStores) {
    // Pour chaque date, calculer le total des magasins en valeurs absolues
    const totalStoresAbsolute = allLabels.map(date => {
      let totalStores = 0;
      let productsWithData = 0;

      // Calculer le total pour cette semaine
      productsChartData.forEach(product => {
        const weekData = product.data.weeklyData.find(w => w.weekKey === date);
        if (weekData) {
          // Le total des magasins est PRESENT + NOT_PRESENT + NO_REF_SHOPS
          const weekTotal = weekData.PRESENT_SHOPS + weekData.NOT_PRESENT_SHOPS + weekData.NO_REF_SHOPS;
          totalStores += weekTotal;
          productsWithData++;
        }
      });

      // Calculer la moyenne si multi-produits
      const finalTotal = multiProduct && productsWithData > 0 ? totalStores / productsWithData : totalStores;

      // Retourner la valeur absolue
      return Math.round(finalTotal);
    });

    datasets.push({
      type: 'bar',
      label: _t('Store coverage'),
      data: totalStoresAbsolute,
      backgroundColor: colors.bar.backgroundColor,
      borderColor: colors.bar.borderColor,
      borderWidth: 1,
      borderRadius: 4,
      yAxisID: 'y1',
      order: 2
    });
  }

  const chartConfig = {
    labels: allLabels.map(formatDateLabel),
    datasets
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: colors.text
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const index = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            const label = context.dataset.label || '';
            const value = context.parsed.y !== null ? context.parsed.y : 'N/A';

            // Si c'est la couverture des magasins, afficher en valeur absolue
            if (label.includes('Couverture des magasins')) {
              return `${label}: ${Math.round(value)} magasins`;
            } else {
              // Sinon, afficher en pourcentage
              return `${label}: ${typeof value === 'number' ? value.toFixed(2) : value}%`;
            }
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: _t('DN rate (%)'),
          color: colors.text
        },
        grid: {
          color: colors.grid
        },
        ticks: {
          color: colors.text
        }
      },
      y1: {
        type: 'linear',
        display: showVisitedStores,
        position: 'right',
        beginAtZero: true,
        title: {
          display: true,
          text: _t('Number of stores'),
          color: colors.text
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: colors.text
        }
      },
      x: {
        title: {
          display: true,
          text: _t('Week'),
          color: colors.text
        },
        grid: {
          color: colors.grid
        },
        ticks: {
          color: colors.text
        }
      }
    }
  };

  // Toggle class for light/dark mode
  const toggleClass = isDarkMode ? 'bg-gray-300' : 'bg-gray-200';
  const textClass = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const bgClass = isDarkMode ? 'bg-gray-700' : 'bg-white';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        <StatTile
          size="s"
          label={_t('ND')}
          value={`${(aggregatedStats.lastDnRate?.toFixed(2) || 0)}%`}
          color={getDNColorName(aggregatedStats.lastDnRate)}
        />
        <StatTile
          size="s"
          label={_t('Stores with product')}
          value={aggregatedStats.finalStoresWithProduct || 0}
          color="success"
        />
        <StatTile
          size="s"
          label={_t('Stores without product')}
          value={aggregatedStats.totalStores - aggregatedStats.finalStoresWithProduct - aggregatedStats.excludedStoresCount || 0}
          color="warning"
        />
        <StatTile
          size="s"
          label={_t('Stores without reference')}
          value={aggregatedStats.excludedStoresCount}
          color="error"
        />
      </div>

      <div className={`${bgClass} p-4 rounded-lg shadow-lg`} style={{ height: '400px' }}>
        <Line data={chartConfig} options={options} />
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-6 mt-4">
          <div className="flex items-center">
            <span className={`mr-2 text-sm ${textClass}`}>
              {_t('Show coverage:')}
            </span>
            <div className="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
              <input
                type="checkbox"
                name="visits-toggle"
                id="visits-toggle"
                checked={showVisitedStores}
                onChange={() => setShowVisitedStores(prev => !prev)}
                className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
              />
              <label
                htmlFor="visits-toggle"
                className={`toggle-label block overflow-hidden h-6 rounded-full ${toggleClass} cursor-pointer`}
              ></label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DNChart;